/**
 * SpatialHeaderGlyph — clickable spatial-state glyph rendered in a result-grid
 * column header. Three states: native (gray ⌖), declared (blue ⌖), declarable
 * (gray ⌖?). Presentational only — the parent passes a resolved state and an
 * onClick wrapper. Shared by TableExplorerGrid and the Query Editor
 * (useCellRenderer), mirroring JsonHeaderGlyph / EnumHeaderGlyph. In T4 only the
 * `native` state is produced; T18/T19 add declared + declarable.
 */

import React from 'react';

import { GLYPH_DECLARED_CLASS, GLYPH_MUTED_CLASS } from './gridGlyphStyles';

export type SpatialGlyphState = 'native' | 'declared' | 'declarable';

type SpatialHeaderGlyphProps = {
  state: SpatialGlyphState;
  onClick: () => void;
};

const CONFIG: Record<
  SpatialGlyphState,
  { testId: string; label: string; title: string; className: string }
> = {
  declared: {
    testId: 'grid-header-spatial-declared',
    label: '⌖',
    title: 'Declared as spatial — click for details',
    className: GLYPH_DECLARED_CLASS,
  },
  native: {
    testId: 'grid-header-spatial-native',
    label: '⌖',
    title: 'Spatial column — click to configure',
    className: GLYPH_MUTED_CLASS,
  },
  declarable: {
    testId: 'grid-header-spatial-declarable',
    label: '⌖?',
    title: 'Looks spatial — click to declare',
    className: GLYPH_MUTED_CLASS,
  },
};

export const SpatialHeaderGlyph = React.memo(function SpatialHeaderGlyph({
  state,
  onClick,
}: SpatialHeaderGlyphProps) {
  const c = CONFIG[state];
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={c.className}
      title={c.title}
      aria-label={c.title}
      data-testid={c.testId}
    >
      {c.label}
    </button>
  );
});
